/* eslint-disable eqeqeq */
import type { FormModel } from '../autoSaveModel';

/**
 * Phase 6e2b-1 — AccessoryView.makeAutoSize (§49.6:2212-2243 / parity 331)
 * as a pure factory + the desired-face-velocity localStorage side channel
 * (parity 349: key `desired_face_velocity_actual_<id>`).
 *
 * NONE of the 10 6e2b-1 types configures autoSize (per the §51.2 table the
 * users are damper/louver/opening/hood — all 6e2b-2). The chassis carries the
 * full engine (auto-size button binding, `_autoSizeSnapshot`, 3-render
 * disable window) so 6e2b-2 only supplies config:
 *   makeAutoSize({widthField, heightField, getExisting, applyResult,
 *                 widthRatio}) — formula VERBATIM:
 *   height = floor(sqrt(144·airflow / (faceVelocity·widthRatio)));
 *   width = widthRatio·height; both snapped UP to the first CMS row ≥ value.
 */

export interface AutoSizeArgs {
  widthField: string;
  heightField: string;
  getExisting: (model: FormModel) => { existingWidth: unknown; existingHeight: unknown };
  applyResult: (
    data: Record<string, unknown>,
    width: number,
    height: number,
    modelJSON: Record<string, unknown>
  ) => void;
  widthRatio?: number;
}

export type AutoSizeFn = (
  auto: boolean,
  model: FormModel,
  data: Record<string, unknown>,
  faceVelocityInit?: number | string,
  cmsAccessory?: Record<string, unknown>
) => boolean | undefined;

export const DEFAULT_FACE_VELOCITY = '1200';

export const desiredFaceVelocityKey = (accessoryId: number): string =>
  `desired_face_velocity_actual_${accessoryId}`;

export const readDesiredFaceVelocity = (
  accessoryId: number,
  defaultFaceVelocity: string = DEFAULT_FACE_VELOCITY
): string => localStorage.getItem(desiredFaceVelocityKey(accessoryId)) || defaultFaceVelocity;

export const writeDesiredFaceVelocity = (accessoryId: number, value: string): void =>
  localStorage.setItem(desiredFaceVelocityKey(accessoryId), value);

export function makeAutoSize({
  widthField,
  heightField,
  getExisting,
  applyResult,
  widthRatio = 2,
}: AutoSizeArgs): AutoSizeFn {
  return function autoSize(auto, model, data, faceVelocityInit, cmsAccessory) {
    if (!data.airflow_actual) return undefined;

    const { existingWidth, existingHeight } = getExisting(model);
    // Do nothing if already set and not forcing recalculation (:2219).
    if (!auto && (existingWidth || existingHeight)) return undefined;

    const airflow = Number(data.airflow_actual);
    const faceVelocity =
      Number(faceVelocityInit) || Number(model.get('face_velocity_actual')) || 1200;

    const calculatedHeight = Math.floor(Math.sqrt((144 * airflow) / (faceVelocity * widthRatio)));
    const calculatedWidth = widthRatio * calculatedHeight;

    // CMS snap-UP (:2231-2235).
    const cmsWidths = (cmsAccessory?.[widthField] as Array<Record<string, unknown>>) || [];
    const cmsHeights = (cmsAccessory?.[heightField] as Array<Record<string, unknown>>) || [];
    const width = cmsWidths.find((w) => Number(w[widthField]) >= calculatedWidth)?.[widthField];
    const height = cmsHeights.find((h) => Number(h[heightField]) >= calculatedHeight)?.[heightField];

    if (!width || !height) return undefined;
    if (width == existingWidth && height == existingHeight) return undefined;

    applyResult(data, Number(width), Number(height), model.toJSON());
    return true;
  };
}
